// OpenSeatsHome.tsx
// "Open seats" on the player's Home (desktop and mobile): raids with public
// seats that this player QUALIFIES for, and how many are open in their
// regions in total ("you qualify for 3 of 12"). One click takes the seat, or
// sends a request when the raid leader approves seats by hand.
// Server: GET /open-seats, POST /open-seats/:raidId/take (auth/open_seats_routes.js).
//
// A Discord link lands here via #Home?seats (or #OpenSeats): the section
// scrolls itself into view.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { DISPLAY, Icon, tc } from "./alignmentUi";
import { useTimezone } from "./lib/TimezoneContext";
import { shortGameName } from "./lib/gameName";
import { getWowClassColor } from "./wowClassColor";

type Spec = { id: number; label: string; image: string | null; roleKey: string | null };
type Char = { id: string; name: string; className: string | null; parse: number | null; specs: Spec[] };
type SeatRaid = {
  id: string; name: string; gameName: string; startsAt: string;
  sessions: Array<{ startsAt: string; durationMinutes: number }>;
  instances: Array<{ id: number; label: string; image: string | null; size: number | null }>;
  guildName: string; guildTag: string | null; guildLogoUrl: string | null;
  realm: string | null; region: string | null; size: number | null; seatsLeft: number;
  minParse: number | null; autoAccept: boolean; specLocked: boolean;
  lootMethod?: { key: string; label: string; desc: string } | null;
  characters: Char[]; myRequest: { id: string } | null;
  contracts?: SeatContract[];
};
/** A contract this player could take with the seat; `eligible` = characterId -> spec ids that qualify. */
type SeatContract = {
  id: string; kind: string; headline: string; terms: string; version: number;
  seats: number | null; seatsLeft: number | null; minParse: number | null;
  eligible: Record<string, number[]>;
};
type Resp = { total: number; qualify: number; raids: SeatRaid[]; contractFooter?: string };

const ERRORS: Record<string, string> = {
  not_eligible: "You no longer qualify for this raid (locked out or booked at the same time).",
  raid_full: "Someone just took the last seat.",
  seats_closed: "The raid leader closed these seats.",
  already_requested: "You already asked for a seat in this raid.",
  spec_not_open: "That spec is not open in this raid.",
  terms_changed: "The raid leader just changed these terms. Read them again before accepting.",
  contract_full: "That contract just filled up. Take the seat without it, or pick another.",
  contract_closed: "That contract was withdrawn.",
  contract_not_eligible: "That character or spec does not qualify for this contract.",
  own_guild_contract: "Guild members cannot take their own guild's contracts.",
  terms_not_accepted: "Tick \"I accept these terms\" to take the contract.",
};

function SeatCard({ raid, tz, footer, onDone, onReload }: {
  raid: SeatRaid; tz: string; footer: string; onDone: (text: string) => void; onReload: () => void;
}) {
  const [charId, setCharId] = useState(raid.characters[0]?.id || "");
  const ch = raid.characters.find((c) => c.id === charId) || raid.characters[0];
  const [specId, setSpecId] = useState<number | null>(ch?.specs[0]?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setSpecId(ch?.specs[0]?.id ?? null); }, [ch]);

  // Contracts this character + spec qualifies for. Picking one is optional,
  // and taking it needs an explicit "I accept these terms".
  const contracts = (raid.contracts || []).filter((c) => ch && specId !== null && (c.eligible[ch.id] || []).includes(specId));
  const [contractId, setContractId] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const contract = contracts.find((c) => c.id === contractId) || null;
  useEffect(() => { if (contractId && !contract) { setContractId(null); setAccepted(false); } }, [contractId, contract]);

  const start = DateTime.fromISO(raid.startsAt).setZone(tz);
  const mins = raid.sessions[0]?.durationMinutes || 0;
  const end = start.plus({ minutes: mins });

  const take = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/open-seats/${encodeURIComponent(raid.id)}/take`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contract
          ? { characterId: ch.id, specId, contractId: contract.id, termsVersion: contract.version, acceptTerms: accepted }
          : { characterId: ch.id, specId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        // Stale terms: reload so the new text is what they read and accept.
        if (j.error === "terms_changed") { setAccepted(false); onReload(); }
        throw new Error(ERRORS[j.error] || "Could not take the seat.");
      }
      const withContract = contract ? " The contract is live." : "";
      onDone(j.seated
        ? `You are in ${raid.name} on ${ch.name}.${withContract}`
        : `Request sent to ${raid.guildName}.${contract ? " The contract goes live if they approve you." : ""}`);
    } catch (e: any) {
      setErr(e.message);
      setBusy(false);
    }
  };
  const withdraw = async () => {
    if (!raid.myRequest) return;
    setBusy(true);
    await fetch(`/open-seats/requests/${raid.myRequest.id}`, { method: "DELETE", credentials: "include" }).catch(() => {});
    onDone("Request withdrawn.");
  };

  const logo = raid.guildLogoUrl;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 shadow-[var(--c-shadow-sm)]">
      <div className="flex items-start gap-3">
        <div className="lfm-dark flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black">
          {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : <span className="text-lg font-bold text-white/70">{raid.guildName.charAt(0)}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold text-[var(--c-ink)]">{raid.name}</div>
          <div className="truncate text-[12px] text-[var(--c-ink-3)]">
            {raid.guildTag ? `<${raid.guildTag}> ` : ""}{raid.guildName} · {raid.realm}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
            {raid.seatsLeft} seat{raid.seatsLeft === 1 ? "" : "s"}
          </span>
          {(raid.contracts || []).length > 0 && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-800 dark:text-amber-200">
              📜 {(raid.contracts || []).length} contract{(raid.contracts || []).length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        <span className="font-semibold text-[var(--c-ink)]">{start.toFormat("ccc d LLL · HH:mm")}{mins ? `-${end.toFormat("HH:mm")}` : ""}</span>
        {raid.instances.slice(0, 2).map((i) => (
          <span key={i.id} className="flex items-center gap-1 text-[var(--c-ink-2)]"><Icon src={i.image} alt={i.label} size={18} />{i.label}</span>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 text-[11px] font-semibold text-[var(--c-ink-3)]">
        <span className="rounded-md bg-[var(--c-sunken)] px-1.5 py-0.5">{shortGameName(raid.gameName)}</span>
        {raid.lootMethod && <span title={raid.lootMethod.desc} className="rounded-md bg-purple-500/15 px-1.5 py-0.5 text-purple-800 dark:text-purple-200">Loot: {raid.lootMethod.label}</span>}
        {raid.minParse !== null && <span className="rounded-md bg-[var(--c-sunken)] px-1.5 py-0.5">Parse {raid.minParse}+</span>}
        {raid.specLocked && <span className="rounded-md bg-[var(--c-sunken)] px-1.5 py-0.5">Specific specs</span>}
        <span className="rounded-md bg-[var(--c-sunken)] px-1.5 py-0.5">{raid.autoAccept ? "Instant" : "Leader approves"}</span>
      </div>

      {raid.myRequest ? (
        <div className="flex items-center gap-2">
          <span className="flex-1 text-[13px] font-semibold text-amber-700 dark:text-amber-300">Requested · waiting for the leader</span>
          <button onClick={withdraw} disabled={busy} className="rounded-lg border border-[var(--c-line)] px-3 py-1.5 text-[12px] font-semibold text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)]">Withdraw</button>
        </div>
      ) : (
        <>
        {contracts.length > 0 && (
          <div className="space-y-1.5 rounded-xl bg-[var(--c-sunken)] p-2.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--c-ink-3)]">Contracts you qualify for</div>
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[var(--c-ink-2)]">
              <input type="radio" checked={!contract} onChange={() => { setContractId(null); setAccepted(false); }} className="accent-amber-500" />
              Just the seat, no contract
            </label>
            {contracts.map((c) => (
              <div key={c.id}>
                <label className="flex cursor-pointer items-start gap-2 text-[13px]">
                  <input type="radio" checked={contractId === c.id} onChange={() => { setContractId(c.id); setAccepted(false); }} className="mt-0.5 accent-amber-500" />
                  <span>
                    <span className="font-semibold text-[var(--c-ink)]">📜 {c.headline}</span>
                    <span className="text-[var(--c-ink-4)]"> · {c.seatsLeft === null ? "for everyone who matches" : `${c.seatsLeft} left`}</span>
                  </span>
                </label>
                {contractId === c.id && (
                  <div className="ml-6 mt-1.5 space-y-2">
                    <div className="max-h-40 overflow-y-auto whitespace-pre-line rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-2.5 text-[12px] leading-relaxed text-[var(--c-ink-2)]">
                      {c.terms}
                      {footer && <div className="mt-2 italic text-[var(--c-ink-4)]">{footer}</div>}
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 text-[12px] font-semibold text-[var(--c-ink)]">
                      <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="h-4 w-4 accent-amber-500" />
                      I accept these terms
                    </label>
                    {!raid.autoAccept && <p className="text-[11px] text-[var(--c-ink-4)]">It only becomes your contract if the raid leader approves you.</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {raid.characters.length > 1 ? (
            <select value={charId} onChange={(e) => setCharId(e.target.value)}
              className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-1.5 text-[13px] text-[var(--c-ink)]">
              {raid.characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : (
            <span className="text-[13px] font-semibold" style={{ color: tc(getWowClassColor(ch?.className)) }}>{ch?.name}</span>
          )}
          {ch && ch.specs.length > 1 ? (
            <div className="flex gap-1">
              {ch.specs.map((s) => (
                <button key={s.id} type="button" title={s.label} onClick={() => setSpecId(s.id)} aria-pressed={specId === s.id}
                  className={`rounded-lg border p-0.5 ${specId === s.id ? "border-[var(--c-accent)]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                  <Icon src={s.image} alt={s.label} size={24} />
                </button>
              ))}
            </div>
          ) : ch?.specs[0] ? (
            <span className="flex items-center gap-1 text-[12px] text-[var(--c-ink-3)]"><Icon src={ch.specs[0].image} alt={ch.specs[0].label} size={20} />{ch.specs[0].label}</span>
          ) : null}
          <button onClick={take} disabled={busy || !ch || (!!contract && !accepted)}
            className="ml-auto rounded-xl bg-[var(--c-accent)] px-4 py-2 text-[13px] font-bold text-[var(--c-ink-on-fill)] transition hover:bg-[var(--c-accent-hover)] disabled:opacity-50 dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500">
            {raid.autoAccept ? "Take seat" : "Request seat"}{contract ? " with contract" : ""}
          </button>
        </div>
        </>
      )}
      {err && <p className="text-[12px] text-[var(--c-bad-ink)]">{err}</p>}
    </div>
  );
}

export default function OpenSeatsHome({ onChanged, variant = "desktop" }: { onChanged?: () => void; variant?: "desktop" | "mobile" }) {
  const tz = useTimezone();
  const [data, setData] = useState<Resp | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLElement | null>(null);

  const load = useCallback(() => {
    fetch("/open-seats", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      // Anything not shaped like the endpoint (an old server, a proxy error page)
      // hides the section instead of crashing Home.
      .then((d) => setData(d && Array.isArray(d.raids) && typeof d.total === "number" ? d : null))
      .catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep link from Discord: scroll here once the list has rendered.
  useEffect(() => {
    if (!data || !ref.current) return;
    const h = window.location.hash.toLowerCase();
    if (h.includes("seats")) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [data]);

  if (!data || data.total === 0) return null;
  const list = showAll ? data.raids : data.raids.slice(0, 4);

  return (
    // The mobile app is always dark and has no palette of its own: lfm-dark
    // pins the theme tokens to the dark set there.
    <section ref={ref} aria-label="Open seats" id="open-seats" className={variant === "mobile" ? "lfm-dark scroll-mt-4 px-4" : "scroll-mt-24"}>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className={`${variant === "mobile" ? "text-xl" : "text-2xl"} text-[var(--c-ink)]`} style={DISPLAY}>Open seats</h2>
        <span className="text-[14px] text-[var(--c-ink-3)]">
          You qualify for <b className="text-[var(--c-ink)]">{data.qualify}</b> of {data.total} open raid{data.total === 1 ? "" : "s"} in your region{data.total === 1 ? "" : "s"}
        </span>
      </div>
      {note && <p className="mb-3 text-[13px] font-semibold text-emerald-700 dark:text-emerald-400">{note}</p>}
      {data.raids.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--c-line)] px-4 py-3 text-[13px] text-[var(--c-ink-3)]">
          None fit your characters right now: realm, faction, spec, parse, lockouts or a clash with a raid you are already in.
        </div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            {list.map((r) => (
              <SeatCard key={r.id} raid={r} tz={tz} footer={data.contractFooter || ""} onReload={load}
                onDone={(text) => { setNote(text); load(); onChanged?.(); }} />
            ))}
          </div>
          {data.raids.length > 4 && (
            <button onClick={() => setShowAll((s) => !s)} className="mt-3 text-[13px] font-semibold text-[var(--c-accent-ink)] hover:underline">
              {showAll ? "Show fewer" : `Show all ${data.raids.length}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
